/**
 * AF-13 — per-hunk diff engine (webIDE port).
 *
 * Source of truth + unit tests live in `@askfinz/agent-core` (`src/hunk-diff.ts`, 9 vitest cases,
 * proven end-to-end through the CLI standalone binary). The webIDE can't consume the agent-core
 * `file:` workspace dep at build time, so this is a verbatim port of that tested logic — keep the
 * two in sync. Pure: no React/DOM/IO, so it runs identically in the browser and in Node.
 *
 *   • computeHunks(old, new, context) — LCS line-diff grouped into non-overlapping hunks.
 *   • applyHunks(old, hunks, accepted) — reconstruct applying ONLY accepted hunks (reject = keep old).
 *   • summarizeHunks(hunks) — { hunks, added, removed } for the review badge.
 */

export interface Hunk {
  id: string
  oldStart: number
  oldCount: number
  oldLines: string[]
  newLines: string[]
  contextBefore: string[]
  contextAfter: string[]
}

type Op = { t: "eq"; o: number; n: number } | { t: "del"; o: number } | { t: "ins"; n: number }

function toLines(text: string): string[] {
  return text.split("\n")
}

function lcsOps(a: string[], b: string[]): Op[] {
  const n = a.length
  const m = b.length
  const dp: Int32Array[] = Array.from({ length: n + 1 }, () => new Int32Array(m + 1))
  for (let i = n - 1; i >= 0; i--) {
    const row = dp[i]!
    const next = dp[i + 1]!
    const ai = a[i]
    for (let j = m - 1; j >= 0; j--) {
      row[j] = ai === b[j] ? next[j + 1]! + 1 : Math.max(next[j]!, row[j + 1]!)
    }
  }
  const ops: Op[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ t: "eq", o: i, n: j })
      i++
      j++
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      ops.push({ t: "del", o: i })
      i++
    } else {
      ops.push({ t: "ins", n: j })
      j++
    }
  }
  while (i < n) ops.push({ t: "del", o: i++ })
  while (j < m) ops.push({ t: "ins", n: j++ })
  return ops
}

export function computeHunks(oldText: string, newText: string, context = 3): Hunk[] {
  const a = toLines(oldText)
  const b = toLines(newText)
  const ops = lcsOps(a, b)
  const hunks: Hunk[] = []
  let oldConsumed = 0
  let k = 0
  while (k < ops.length) {
    const head = ops[k]
    if (!head) break
    if (head.t === "eq") {
      oldConsumed++
      k++
      continue
    }
    const oldStart = oldConsumed
    const removed: string[] = []
    const added: string[] = []
    for (let op = ops[k]; op && op.t !== "eq"; op = ops[k]) {
      if (op.t === "del") {
        removed.push(a[op.o] ?? "")
        oldConsumed++
      } else {
        added.push(b[op.n] ?? "")
      }
      k++
    }
    const oldEnd = oldStart + removed.length
    hunks.push({
      id: `h${hunks.length}`,
      oldStart,
      oldCount: removed.length,
      oldLines: removed,
      newLines: added,
      contextBefore: a.slice(Math.max(0, oldStart - context), oldStart),
      contextAfter: a.slice(oldEnd, oldEnd + context),
    })
  }
  return hunks
}

export function applyHunks(oldText: string, hunks: Hunk[], accepted: Set<string> | string[]): string {
  const acc = accepted instanceof Set ? accepted : new Set(accepted)
  const a = toLines(oldText)
  const out: string[] = []
  const sorted = [...hunks].sort((x, y) => x.oldStart - y.oldStart)
  let i = 0
  for (const h of sorted) {
    if (i < h.oldStart) out.push(...a.slice(i, h.oldStart))
    out.push(...(acc.has(h.id) ? h.newLines : h.oldLines))
    i = h.oldStart + h.oldCount
  }
  if (i < a.length) out.push(...a.slice(i))
  return out.join("\n")
}

export function summarizeHunks(hunks: Hunk[]): { hunks: number; added: number; removed: number } {
  return {
    hunks: hunks.length,
    added: hunks.reduce((s, h) => s + h.newLines.length, 0),
    removed: hunks.reduce((s, h) => s + h.oldLines.length, 0),
  }
}
